import { useEffect, useState } from "react";
import { Users, Plus, Trash2, Pencil, Eye, EyeOff, ShieldAlert, IdCard } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useStepUp } from "../StepUpGuard";
import TravelerProfile from "./TravelerProfile";

const EMPTY = { name: "", relation: "", phone: "", pnr: "", notes: "", dob: "", passport_no: "", passport_country: "", passport_expiry: "", tsa_pre: "", clear: "", redress: "" };

export default function TravelersManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showSensitive, setShowSensitive] = useState(false);
  const [ack, setAck] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [profileOpen, setProfileOpen] = useState({});
  const [deleting, setDeleting] = useState(null); // {id, refs} pending delete decision

  const load = () => api.get("/trips/travelers").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setShowSensitive(false); setAck(false); setEditing("new"); };
  const openEdit = (t) => {
    const clean = Object.fromEntries(Object.entries(t).map(([k, v]) => [k, v ?? ""]));
    setForm({ ...EMPTY, ...clean }); setShowSensitive(t.has_sensitive); setAck(t.has_sensitive); setEditing(t.id);
  };
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    const payload = { ...form };
    if (editing !== "new") payload.id = editing;
    if (!showSensitive) ["dob", "passport_no", "passport_country", "passport_expiry", "tsa_pre", "clear", "redress"].forEach((k) => delete payload[k]);
    try { await api.post("/trips/travelers", payload); setEditing(null); load(); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const del = async (id) => {
    // Delete care: if things are assigned to this traveller, ask what to do first.
    try {
      const r = await api.get(`/trips/travelers/${id}/profile`);
      const p = r.data;
      const refs = p.bags.length + p.gear_bags.length + p.gear_items.length + p.programs.length +
        p.visa_links.length + p.insurance.length + p.wallet_items.length;
      if (refs > 0) { setDeleting({ id, refs, p }); return; }
    } catch {}
    await confirmDel(id, false);
  };
  const confirmDel = async (id, unassign) => {
    try {
      await api.delete(`/trips/travelers/${id}`, { params: unassign ? { unassign: true } : {} });
      toast.success(unassign ? "Traveller removed and their items unassigned" : "Traveller removed — records kept their name");
      setDeleting(null); load();
    } catch { toast.error("Could not remove traveller"); }
  };
  const guard = useStepUp();
  const reveal = async (id) => {
    if (revealed[id]) { setRevealed((r) => ({ ...r, [id]: null })); return; }
    guard(async () => {
      try { const { data } = await api.get(`/trips/travelers/${id}/reveal`); setRevealed((r) => ({ ...r, [id]: data })); } catch {}
    });
  };

  return (
    <div data-testid="travelers-manager">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-900 text-[#34A853] text-2xl flex items-center gap-2"><Users className="w-6 h-6" /> Travellers & Family</h2>
        <button onClick={openNew} data-testid="traveler-new" className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700"><Plus className="w-4 h-4" /> Add traveller</button>
      </div>

      {editing && (
        <div className="border border-primary bg-blue-50/40 p-4 mb-4" data-testid="traveler-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={form.name} onChange={f("name")} placeholder="Full name" data-testid="traveler-name" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <input value={form.relation} onChange={f("relation")} placeholder="Relation (e.g. Father, Spouse)" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <input value={form.phone} onChange={f("phone")} type="tel" placeholder="Phone / WhatsApp (+1 555 123 4567)" data-testid="traveler-phone" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <input value={form.pnr} onChange={f("pnr")} placeholder="Booking code / PNR" data-testid="traveler-pnr" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <input value={form.notes} onChange={f("notes")} placeholder="Notes (seat pref, meal, contact…)" className="border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>

          <label className="flex items-start gap-2 mt-3 text-sm cursor-pointer" data-testid="traveler-sensitive-toggle">
            <input type="checkbox" checked={showSensitive} onChange={(e) => { setShowSensitive(e.target.checked); if (!e.target.checked) setAck(false); }} className="mt-1" />
            <span>Add sensitive travel documents (passport, DOB, TSA Pre, CLEAR) — <span className="text-amber-700">optional</span></span>
          </label>

          {showSensitive && (
            <div className="mt-2 border border-amber-200 bg-amber-50 p-3">
              <label className="flex items-start gap-2 text-xs text-amber-800 cursor-pointer" data-testid="traveler-ack">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
                <span><ShieldAlert className="w-3.5 h-3.5 inline mr-1" />This is a demo environment (data not encrypted at rest). I understand and won't store real passport numbers I don't want stored.</span>
              </label>
              {ack && (
                <div className="grid sm:grid-cols-2 gap-2 mt-3">
                  <input value={form.dob} onChange={f("dob")} type="date" placeholder="Date of birth" className="border border-zinc-300 px-3 py-2 text-sm" />
                  <input value={form.passport_no} onChange={f("passport_no")} placeholder="Passport number" className="border border-zinc-300 px-3 py-2 text-sm" />
                  <input value={form.passport_country} onChange={f("passport_country")} placeholder="Passport country" className="border border-zinc-300 px-3 py-2 text-sm" />
                  <input value={form.passport_expiry} onChange={f("passport_expiry")} type="date" placeholder="Passport expiry" className="border border-zinc-300 px-3 py-2 text-sm" />
                  <input value={form.tsa_pre} onChange={f("tsa_pre")} placeholder="TSA Pre number" className="border border-zinc-300 px-3 py-2 text-sm" />
                  <input value={form.clear} onChange={f("clear")} placeholder="CLEAR number" className="border border-zinc-300 px-3 py-2 text-sm" />
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
            <button onClick={save} data-testid="traveler-save" className="px-4 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700">Save traveller</button>
          </div>
        </div>
      )}

      {items.length === 0 && !editing ? <p className="text-sm text-muted-foreground">No travellers yet. Add family members or co-travellers with their PNR and (optionally) documents.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((t) => {
            const rv = revealed[t.id];
            return (
              <div key={t.id} className="border border-zinc-300 bg-white p-4" data-testid={`traveler-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div><div className="font-heading font-900">{t.name}</div>{t.relation && <div className="text-xs text-muted-foreground">{t.relation}</div>}</div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(t)} data-testid={`traveler-edit-${t.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(t.id)} data-testid={`traveler-delete-${t.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {t.phone && <a href={`https://wa.me/${t.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid={`traveler-whatsapp-${t.id}`} className="text-xs font-mono mt-2 block text-emerald-700 hover:underline">WhatsApp: {t.phone}</a>}
                {t.pnr && <div className="text-xs font-mono mt-2">PNR: <span className="text-zinc-900">{t.pnr}</span></div>}
                {t.notes && <div className="text-sm text-zinc-600 mt-1">{t.notes}</div>}
                {t.passport_alert && (
                  <div className="mt-2 border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 flex items-start gap-1.5" data-testid={`traveler-passport-alert-${t.id}`}>
                    <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{t.passport_alert.message}</span>
                  </div>
                )}
                {deleting?.id === t.id && (
                  <div className="mt-2 border border-rose-300 bg-rose-50 p-2.5 text-xs" data-testid={`traveler-delete-care-${t.id}`}>
                    <div className="font-700 text-rose-800 mb-1.5">
                      {deleting.refs} item{deleting.refs === 1 ? " is" : "s are"} (bags, programs, policies…) assigned to {t.name}. What should happen to {deleting.refs === 1 ? "it" : "them"}?
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => confirmDel(t.id, true)} data-testid={`traveler-delete-unassign-${t.id}`} className="px-2.5 py-1.5 bg-rose-600 text-white font-600">Unassign &amp; remove</button>
                      <button onClick={() => confirmDel(t.id, false)} data-testid={`traveler-delete-keep-${t.id}`} className="px-2.5 py-1.5 border border-rose-300 text-rose-700 font-600">Keep names on records &amp; remove</button>
                      <button onClick={() => setDeleting(null)} className="px-2.5 py-1.5 border border-zinc-300">Cancel</button>
                    </div>
                  </div>
                )}
                <button onClick={() => setProfileOpen((p) => ({ ...p, [t.id]: !p[t.id] }))} data-testid={`traveler-profile-btn-${t.id}`}
                  className="mt-2 inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-primary text-primary">
                  <IdCard className="w-3.5 h-3.5" /> {profileOpen[t.id] ? "Hide profile" : "View profile"}
                </button>
                {profileOpen[t.id] && <TravelerProfile travelerId={t.id} />}
                {t.has_sensitive && (
                  <div className="mt-2 border-t border-zinc-100 pt-2 text-xs font-mono space-y-0.5">
                    <button onClick={() => reveal(t.id)} data-testid={`traveler-reveal-${t.id}`} className="text-primary inline-flex items-center gap-1 mb-1">{rv ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}{rv ? "Hide" : "Reveal"} documents</button>
                    <div>Passport: {(rv || t).passport_no || "—"}</div>
                    <div>TSA Pre: {(rv || t).tsa_pre || "—"} · CLEAR: {(rv || t).clear || "—"}</div>
                    {(rv || t).dob && <div>DOB: {(rv || t).dob}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
